import { useState } from "react";
import {
  Alert,
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";

import type { SessionContext } from "@thalamos/haven-common";
import {
  membershipLabel,
  sessionContextKey,
  sessionContextsFromMemberships,
} from "@thalamos/haven-common";

import api from "../../api/index.js";
import { useUser } from "../../Context/UserContext.js";
import { consumeReturnTo } from "../../routing/returnTo.js";

/**
 * Whether the server let the user act in the context they chose.
 *
 * Named rather than a boolean because the caller has to render the difference,
 * and a bare `false` at a call site does not say what failed.
 */
export type SessionContextChoiceOutcome = "accepted" | "refused";

export const SessionContextSelectorPage = () => {
  const { user, refetchUser } = useUser();
  const navigate = useNavigate();
  const { search } = useLocation();

  const choose = async (
    context: SessionContext,
  ): Promise<SessionContextChoiceOutcome> => {
    const response = await api.user.setSessionContext(context);

    // 4xx resolves rather than throwing on this axios instance, so a refusal
    // has to be read off the status or the user is navigated on as though it
    // worked — into a context they were just refused.
    if (response.status >= 400) {
      return "refused";
    }

    await refetchUser();
    navigate(consumeReturnTo(search), { replace: true });
    return "accepted";
  };

  return (
    <SessionContextSelectorPageContent
      contexts={sessionContextsFromMemberships(user?.memberships ?? [])}
      onChoose={choose}
    />
  );
};

/**
 * The picker itself, with the contexts to offer and the act of choosing one
 * injected rather than reached for through context and the API client. Mounting
 * this needs neither a session nor a router, so a story can show any state the
 * page has — including a refusal — without standing up either.
 */
export const SessionContextSelectorPageContent = ({
  contexts,
  onChoose,
}: {
  contexts: SessionContext[];
  onChoose: (context: SessionContext) => Promise<SessionContextChoiceOutcome>;
}) => {
  const { t } = useTranslation();
  const [selectedIndex, setSelectedIndex] = useState<number | undefined>(
    undefined,
  );
  const [submitting, setSubmitting] = useState(false);
  const [refused, setRefused] = useState(false);

  // Selection is held as a position, not as an organisation id: a user may do
  // two kinds of work at the same organisation, so an id identifies a row's
  // organisation but not the row.
  const selected =
    selectedIndex === undefined ? undefined : contexts[selectedIndex];

  const choose = (context: SessionContext) => {
    setSubmitting(true);
    setRefused(false);

    void onChoose(context)
      .then((outcome) => {
        setRefused(outcome === "refused");
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: 3,
      }}
    >
      <Paper variant="outlined" sx={{ p: 4, maxWidth: 480, width: "100%" }}>
        <Stack spacing={3}>
          <Stack spacing={1}>
            <Typography variant="h5" component="h1">
              {t("sessionContextSelector.title")}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t("sessionContextSelector.description")}
            </Typography>
          </Stack>

          <FormControl fullWidth>
            <InputLabel id="session-context-selector-label">
              {t("sessionContextSelector.label")}
            </InputLabel>
            <Select
              labelId="session-context-selector-label"
              label={t("sessionContextSelector.label")}
              // A controlled Select needs a string for "nothing chosen"; giving
              // it `undefined` makes it uncontrolled instead.
              value={selectedIndex === undefined ? "" : String(selectedIndex)}
              onChange={(event) => {
                setSelectedIndex(Number(event.target.value));
              }}
            >
              {contexts.map((context, index) => (
                <MenuItem
                  key={sessionContextKey(context)}
                  value={String(index)}
                >
                  {/* The kind's name, not the value stored for it: nobody
                      chooses a context called `organisation_administration`. */}
                  {`${membershipLabel(context)} — ${t(`sessionContextKinds.${context.kind}`)}`}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {refused ? (
            <Alert severity="error">{t("errors.sessionContextFailed")}</Alert>
          ) : null}

          <Button
            variant="contained"
            disabled={selected === undefined || submitting}
            onClick={() => {
              if (selected) {
                choose(selected);
              }
            }}
          >
            {t("sessionContextSelector.continue")}
          </Button>
        </Stack>
      </Paper>
    </Box>
  );
};
